import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { invitationCodeService } from '../services/invitationCode';
import './InvitationCodeManagement.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const InvitationCodeManagement = () => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [statistics, setStatistics] = useState(null);

  // 获取当前用户名
  const getCurrentUsername = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).username : '';
  };

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const username = getCurrentUsername();
      const response = await invitationCodeService.getStatistics(username);
      if (response?.code && response.code !== 200) {
        throw new Error(response.message || '统计暂不可用');
      }
      const data = response?.data ?? response;
      setStatistics(
        data
          ? {
              total: data.total ?? data.totals ?? null,
              unused: data.available ?? data.unused ?? null,
              used: data.used ?? null,
              expired: data.expired ?? data.expiredCount ?? null,
            }
          : null,
      );
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error(`获取统计数据失败: ${error.message || '网络错误'}`);
      setStatistics(null);
    }
  };

  // 获取邀请码列表
  const fetchInvitationCodes = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: (params.page || currentPage) - 1, // 后端分页从0开始
        size: params.size || pageSize,
        ...params.filters,
      };

      const response = await invitationCodeService.queryByPage(queryParams);
      console.log('邀请码API响应:', response);
      console.log('响应数据类型:', typeof response);
      console.log('响应是否为数组:', Array.isArray(response));
      console.log('响应data字段:', response?.data);
      console.log('响应data是否为数组:', Array.isArray(response?.data));

      // 处理响应数据 - 根据实际的API响应结构
      console.log('完整响应:', response);
      if (response && response.data) {
        // 处理标准包装格式 {code, message, data: {data: [...], total: ...}}
        if (response.data.data && Array.isArray(response.data.data)) {
          console.log('使用response.data.data作为数据源:', response.data.data);
          setDataSource(response.data.data);
          setTotal(response.data.total || response.data.data.length);
          // 如果有分页信息，更新当前页和页面大小
          if (response.data.page !== undefined) {
            setCurrentPage(response.data.page + 1); // 后端从0开始，前端从1开始
          }
          if (response.data.size !== undefined) {
            setPageSize(response.data.size);
          }
        } else if (Array.isArray(response.data)) {
          // 如果response.data是数组
          console.log('使用response.data作为数据源:', response.data);
          setDataSource(response.data);
          setTotal(response.data.length);
        } else if (
          response.data.content &&
          Array.isArray(response.data.content)
        ) {
          // 如果是分页格式
          console.log('使用分页格式数据:', response.data.content);
          setDataSource(response.data.content || []);
          setTotal(response.data.totalElements || 0);
        } else {
          console.log('尝试将整个data对象作为数据源');
          setDataSource([response.data]);
          setTotal(1);
        }
      } else if (response && Array.isArray(response)) {
        // 提取每个响应项中的data字段
        const invitationCodes = response
          .map((item) => item.data)
          .filter(Boolean);
        console.log('提取的邀请码数据:', invitationCodes);
        setDataSource(invitationCodes);
        setTotal(invitationCodes.length);
      } else {
        console.warn('邀请码响应数据格式不正确:', response);
        setDataSource([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('获取邀请码列表失败:', error);
      message.error(`获取邀请码列表失败: ${error.message || '网络错误'}`);
      setDataSource([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 创建邀请码
  const handleCreateOrUpdate = async (values) => {
    try {
      const username = getCurrentUsername();
      const submitData = {
        remark: values.remark,
        expireTime: values.expireTime ? values.expireTime.toISOString() : null,
      };

      const response = await invitationCodeService.create(submitData, username);
      console.log('创建邀请码响应:', response);

      // 检查响应是否成功
      if (response && (response.code === 200 || response.success)) {
        message.success('创建成功');
        setIsModalVisible(false);
        form.resetFields();
        fetchInvitationCodes();
        fetchStatistics();
      } else {
        message.error(response?.message || '创建邀请码失败');
      }
    } catch (error) {
      console.error('创建邀请码失败:', error);
      message.error(
        `创建邀请码失败: ${error.response?.data?.message || error.message || '网络错误'}`,
      );
    }
  };

  // 删除邀请码
  const handleDelete = async (id) => {
    try {
      const username = getCurrentUsername();
      const response = await invitationCodeService.deleteById(id, username);
      console.log('删除邀请码响应:', response);

      // 检查响应是否成功
      if (response && (response.code === 200 || response.success)) {
        message.success('删除成功');
        fetchInvitationCodes();
        fetchStatistics();
      } else {
        message.error(response?.message || '删除邀请码失败');
      }
    } catch (error) {
      console.error('删除邀请码失败:', error);
      message.error(
        `删除邀请码失败: ${error.response?.data?.message || error.message || '网络错误'}`,
      );
    }
  };

  // 搜索
  const handleSearch = (values) => {
    const filters = {};

    if (values.code) filters.code = values.code;
    if (values.usedUsername) filters.usedUsername = values.usedUsername;
    if (values.status !== undefined) filters.status = values.status;
    if (values.dateRange) {
      filters.createTimeStart = values.dateRange[0].format(
        'YYYY-MM-DD HH:mm:ss',
      );
      filters.createTimeEnd = values.dateRange[1].format('YYYY-MM-DD HH:mm:ss');
    }

    fetchInvitationCodes({ page: 1, filters });
    setCurrentPage(1);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    fetchInvitationCodes({ page: 1 });
    setCurrentPage(1);
  };

  // 打开创建模态框
  const openCreateModal = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  // 状态标签
  const getStatusTag = (status) => {
    const statusConfig = {
      0: { color: 'green', text: '未使用' },
      1: { color: 'blue', text: '已使用' },
      2: { color: 'red', text: '已过期' },
    };
    const config = statusConfig[status] || { color: 'default', text: '未知' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '邀请码',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (text) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
          {text}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    {
      title: '使用者',
      dataIndex: 'usedUsername',
      key: 'usedUsername',
      width: 120,
      render: (username) => username || '-',
    },
    {
      title: '过期时间',
      dataIndex: 'expireTime',
      key: 'expireTime',
      width: 160,
      render: (time) => {
        if (!time) return '-';
        // 处理数组格式的时间 [2025, 11, 7, 14, 50, 25]
        if (Array.isArray(time)) {
          // 检查数组是否包含足够的元素
          if (time.length >= 3) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = time;
            return moment({
              year,
              month: month - 1,
              day,
              hour,
              minute,
              second,
            }).format('YYYY-MM-DD HH:mm');
          } else {
            return '-';
          }
        }
        // 处理字符串格式的时间
        if (typeof time === 'string') {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        // 处理对象格式的时间
        if (typeof time === 'object' && time !== null) {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        return '-';
      },
    },
    {
      title: '使用时间',
      dataIndex: 'usedTime',
      key: 'usedTime',
      width: 160,
      render: (time) => {
        if (!time) return '-';
        // 处理数组格式的时间 [2025, 11, 7, 14, 50, 25]
        if (Array.isArray(time)) {
          // 检查数组是否包含足够的元素
          if (time.length >= 3) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = time;
            return moment({
              year,
              month: month - 1,
              day,
              hour,
              minute,
              second,
            }).format('YYYY-MM-DD HH:mm');
          } else {
            return '-';
          }
        }
        // 处理字符串格式的时间
        if (typeof time === 'string') {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        // 处理对象格式的时间
        if (typeof time === 'object' && time !== null) {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        return '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (time) => {
        if (!time) return '-';
        // 处理数组格式的时间 [2025, 11, 7, 14, 50, 25]
        if (Array.isArray(time)) {
          // 检查数组是否包含足够的元素
          if (time.length >= 3) {
            const [year, month, day, hour = 0, minute = 0, second = 0] = time;
            return moment({
              year,
              month: month - 1,
              day,
              hour,
              minute,
              second,
            }).format('YYYY-MM-DD HH:mm');
          } else {
            return '-';
          }
        }
        // 处理字符串格式的时间
        if (typeof time === 'string') {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        // 处理对象格式的时间
        if (typeof time === 'object' && time !== null) {
          return moment(time).format('YYYY-MM-DD HH:mm');
        }
        return '-';
      },
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Popconfirm
            title="确定要删除这个邀请码吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchInvitationCodes();
    fetchStatistics();
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page-container invitation-code-management">
      <header className="page-header">
        <div>
          <h1 className="page-title">邀请码管理</h1>
          <p className="page-description">
            为已对接客户发放邀请，核对使用情况。
          </p>
        </div>
      </header>
      <Card className="management-card">
        {/* 统计信息 */}
        <Row gutter={16} className="statistics-row">
          <Col span={6}>
            <Statistic
              title="总数"
              value={statistics?.total ?? '-'}
              prefix={<KeyOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="未使用"
              value={statistics?.unused ?? '-'}
              valueStyle={{ color: 'var(--ink)' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已使用"
              value={statistics?.used ?? '-'}
              valueStyle={{ color: 'var(--ink)' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="已过期"
              value={statistics?.expired ?? '-'}
              valueStyle={{ color: 'var(--ink)' }}
            />
          </Col>
        </Row>

        {/* 搜索区域 */}
        <Card size="small" className="search-card">
          <Form form={searchForm} layout="inline" onFinish={handleSearch}>
            <Form.Item name="code" label="邀请码">
              <Input placeholder="请输入邀请码" />
            </Form.Item>
            <Form.Item name="usedUsername" label="使用用户">
              <Input placeholder="请输入用户名" />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select
                placeholder="请选择状态"
                style={{ width: 120 }}
                allowClear
              >
                <Option value={0}>未使用</Option>
                <Option value={1}>已使用</Option>
                <Option value={2}>已过期</Option>
              </Select>
            </Form.Item>
            <Form.Item name="dateRange" label="创建时间">
              <RangePicker showTime />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            新建邀请码
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchInvitationCodes()}
          >
            刷新
          </Button>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建模态框 */}
      <Modal
        title="新建邀请码"
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateOrUpdate}>
          <Form.Item
            name="expireTime"
            label="过期时间"
            rules={[{ required: true, message: '请选择过期时间!' }]}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="请选择过期时间"
            />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input.TextArea
              rows={3}
              placeholder="请输入备注信息（可选）"
              maxLength={500}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                创建邀请码
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InvitationCodeManagement;
